import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { formatDistanceToNow, format } from 'date-fns';
import { api, Article as ArticleType } from '../utils/api';
import { MarkdownRenderer } from '../components/MarkdownRenderer';
import { Comments } from '../components/Comments';
import { TableOfContents } from '../components/TableOfContents';
import { LanguageToggle } from '../components/LanguageToggle';
import { RelatedArticles } from '../components/RelatedArticles';
import { SEO } from '../components/SEO';
import { createArticleSchema, createBreadcrumbSchema } from '../utils/schema';
import { useAuthStore } from '../stores/auth';
import { useLanguageStore } from '../stores/language';
import { tooltips } from '../config/tooltips';

export function Article() {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<ArticleType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { user } = useAuthStore();
  const { language } = useLanguageStore();

  useEffect(() => {
    const fetchArticle = async () => {
      if (!slug) return;

      setLoading(true);
      setError(null);

      try {
        const { article } = await api.getArticle(slug);
        setArticle(article);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load article');
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">
          {error || 'Article not found'}
        </h1>
        <Link to="/" className="text-primary-600 hover:text-primary-700">
          Go back home
        </Link>
      </div>
    );
  }

  const canEdit = user && (user.id === article.author_id || user.role === 'admin');
  const publishDate = article.published_at || article.created_at;

  // Check if Chinese content exists
  const hasChineseContent = !!(article.title_zh && article.content_zh);

  // Get content based on language preference
  const displayTitle = (language === 'zh' && article.title_zh) ? article.title_zh : article.title;
  const displayContent = (language === 'zh' && article.content_zh) ? article.content_zh : article.content;

  // Generate SEO metadata
  const metaDescription =
    article.excerpt || displayContent.replace(/[#*`\[\]]/g, '').substring(0, 160) + '...';
  const articleSchema = createArticleSchema(article);
  const breadcrumbSchema = createBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: displayTitle, url: `/article/${article.slug}` },
  ]);

  return (
    <>
      <SEO
        title={displayTitle}
        description={metaDescription}
        canonical={`/article/${article.slug}`}
        ogType="article"
        ogImage={article.cover_image}
        ogImageAlt={displayTitle}
        article={{
          publishedTime: article.published_at || article.created_at,
          modifiedTime: article.updated_at,
          author: article.author_name,
          tags: article.parsedTags,
          section: article.parsedTags?.[0],
        }}
        schema={[articleSchema, breadcrumbSchema]}
      />

      {/* Table of Contents - Fixed sidebar on large screens */}
      <TableOfContents content={displayContent} />

      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <header className="mb-8">
          {article.visibility !== 'public' && (
            <div className="mb-4">
              <span className={`badge ${article.visibility === 'private' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                {article.visibility === 'private' ? 'Private' : 'Unlisted'}
              </span>
            </div>
          )}

          <div className="flex items-center justify-between gap-4 mb-4">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100">{displayTitle}</h1>
            <LanguageToggle hasChineseContent={hasChineseContent} />
          </div>

          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              {article.author_avatar ? (
                <img
                  src={article.author_avatar}
                  alt={article.author_name}
                  className="w-10 h-10 rounded-full"
                />
              ) : (
                <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
                  <span className="text-lg font-medium text-gray-600 dark:text-gray-300">
                    {article.author_name?.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">{article.author_name}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {format(new Date(publishDate), 'MMM d, yyyy')} · {article.view_count} views
                </p>
              </div>
            </div>

            {canEdit && (
              <Link
                to={`/edit/${article.id}`}
                className="btn-secondary p-2"
                title={tooltips.editArticle}
                aria-label={tooltips.editArticle}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </Link>
            )}
          </div>

          {article.parsedTags && article.parsedTags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {article.parsedTags.map((tag) => (
                <Link
                  key={tag}
                  to={`/tag/${encodeURIComponent(tag)}`}
                  className="badge-primary hover:bg-primary-200"
                >
                  #{tag}
                </Link>
              ))}
            </div>
          )}
        </header>

        {/* Content */}
        <div className="prose prose-lg max-w-none dark:prose-invert">
          <MarkdownRenderer content={displayContent} />
        </div>

        {/* Footer */}
        <footer className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
            <span>
              Last updated {formatDistanceToNow(new Date(article.updated_at), { addSuffix: true })}
            </span>
            <Link to="/" className="text-primary-600 hover:text-primary-700">
              Back to articles
            </Link>
          </div>
        </footer>

        {/* Related Articles */}
        {article.parsedTags && article.parsedTags.length > 0 && (
          <RelatedArticles
            currentArticleId={article.id}
            tags={article.parsedTags}
          />
        )}

        {/* Comments */}
        <Comments articleId={article.id} articleAuthorId={article.author_id} />
      </article>
    </>
  );
}
